import React, { useEffect, useRef, useState } from 'react'
import type { Review, ReviewPhoto } from '../api/review'
import { adminReviewApi } from '../api/review'
import { formatScoreOneDecimal } from '../../utils/scoreFormat'

interface AdminReviewDetailModalProps {
  review: Review
  onClose: () => void
  formatDate: (dateString: string) => string
  /** 공개 범위를 바꾸면 목록에도 알린다 */
  onVisibilityChange?: (reviewId: number, isPublic: boolean) => void
}

const AdminReviewDetailModal: React.FC<AdminReviewDetailModalProps> = ({
  review,
  onClose,
  formatDate,
  onVisibilityChange,
}) => {
  // 공개 범위. 켜짐 = 공개, 꺼짐 = 비공개
  const [isPublic, setIsPublic] = useState<boolean>(review.is_public)
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [visibilityError, setVisibilityError] = useState<string | null>(null)

  useEffect(() => {
    setIsPublic(review.is_public)
    setVisibilityError(null)
  }, [review.id, review.is_public])

  const toggleVisibility = async () => {
    if (savingVisibility) return
    const next = !isPublic
    setSavingVisibility(true)
    setVisibilityError(null)
    try {
      const res = await adminReviewApi.updateReviewVisibility(review.id, next)
      setIsPublic(res.is_public)
      onVisibilityChange?.(review.id, res.is_public)
    } catch (e: any) {
      setVisibilityError(
        e?.response?.data?.message || '공개 범위를 바꾸지 못했습니다'
      )
    } finally {
      setSavingVisibility(false)
    }
  }

  // 사진만 이 화면에서 바뀌므로 목록도 여기서 들고 있는다.
  // 리뷰가 바뀌면(다른 행을 열면) 새 목록으로 갈아 끼운다.
  const [photos, setPhotos] = useState<ReviewPhoto[]>(
    (review.photos as ReviewPhoto[]) ?? []
  )
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const replacingIndex = useRef<number | null>(null)

  useEffect(() => {
    setPhotos((review.photos as ReviewPhoto[]) ?? [])
    setError(null)
  }, [review.id, review.photos])

  const pickReplacement = (index: number) => {
    replacingIndex.current = index
    if (fileInput.current) {
      fileInput.current.value = ''
      fileInput.current.click()
    }
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const index = replacingIndex.current
    if (!file || index === null) return

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })

    setBusy(index)
    setError(null)
    try {
      setPhotos(await adminReviewApi.replacePhoto(review.id, index, dataUrl))
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '사진을 교체하지 못했습니다.')
    } finally {
      setBusy(null)
      replacingIndex.current = null
    }
  }

  const handleDelete = async (index: number) => {
    if (!window.confirm(`${index + 1}번째 사진을 뺄까요? 되돌리려면 다시 올려야 합니다.`)) {
      return
    }
    setBusy(index)
    setError(null)
    try {
      setPhotos(await adminReviewApi.deletePhoto(review.id, index))
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '사진을 빼지 못했습니다.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="p-24px">
      <div className="flex gap-24px mb-24px">
        {review.user?.profile_image && (
          <div className="flex-shrink-0">
            <img
              src={review.user.profile_image}
              alt="프로필"
              className={`
                w-120px
                h-120px
                rounded-12px
                object-cover
                border
                border-brandPrimary-10%
              `}
            />
          </div>
        )}
        <div className="flex-1 grid grid-cols-2 gap-16px">
          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              리뷰 ID
            </label>
            <div className="text-style-body2 text-textColorText2">
              {review.id}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              사용자
            </label>
            <div className="text-style-body2 text-textColorText2">
              {review.user?.nickname || review.user?.username || '-'}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              장소
            </label>
            <div className="text-style-body2 text-textColorText2">
              {review.place?.name || '-'}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              점수
            </label>
            <div className="text-style-body2 text-textColorText2">
              {review.score != null
                ? `${formatScoreOneDecimal(review.score)}점`
                : '-'}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              공개 범위
            </label>
            {/* 누르면 바로 바뀐다. 공개 여부만 바뀌고 점수·순위·사진·작성일은 그대로다 */}
            <button
              type="button"
              role="switch"
              aria-checked={isPublic}
              onClick={toggleVisibility}
              disabled={savingVisibility}
              className="flex items-center gap-8px disabled:opacity-50"
            >
              <span
                className={`relative inline-block w-36px h-20px rounded-full transition-colors ${
                  isPublic ? 'bg-brown-80%-main' : 'bg-brandPrimary-20%'
                }`}
              >
                <span
                  className={`absolute top-2px w-16px h-16px rounded-full bg-white shadow transition-all ${
                    isPublic ? 'left-18px' : 'left-2px'
                  }`}
                />
              </span>
              <span className="text-style-body2 text-textColorText2">
                {isPublic ? '공개' : '비공개'}
              </span>
            </button>
            {visibilityError && (
              <div className="mt-4px text-style-captionM text-textColorText2">
                {visibilityError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-24px">
        <label className="text-style-labelS text-textColorText2 mb-4px block">
          한줄평
        </label>
        <div className="text-style-body2 text-textColorText2 whitespace-pre-wrap break-words">
          {review.review_text || '-'}
        </div>
      </div>

      <div className="mb-24px">
        <label className="text-style-labelS text-textColorText2 mb-4px block">
          사진 ({photos.length}장)
        </label>

        {photos.length === 0 ? (
          <div className="text-style-body2 text-textColorText3 mt-8px">없음</div>
        ) : (
          <div className="flex flex-wrap gap-12px mt-8px">
            {photos.map((photo, index) => (
              <div key={photo.id ?? index} className="flex-shrink-0 w-120px">
                <img
                  /* 저장된 필드는 preview다. url을 보던 탓에 그동안 깨져 나왔다 */
                  src={photo.preview}
                  alt={`리뷰 사진 ${index + 1}`}
                  className={`
                    w-120px
                    h-120px
                    rounded-8px
                    object-cover
                    border
                    border-brandPrimary-10%
                    ${busy === index ? 'opacity-40' : ''}
                  `}
                />
                <div className="flex gap-4px mt-4px">
                  <button
                    type="button"
                    onClick={() => pickReplacement(index)}
                    disabled={busy !== null}
                    className={`
                      flex-1 py-4px rounded-6px text-style-button2
                      border border-brandPrimary-20% text-textColorText2
                      hover:bg-4B3621-80%-5% disabled:opacity-40
                    `}
                  >
                    교체
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    disabled={busy !== null}
                    className={`
                      flex-1 py-4px rounded-6px text-style-button2
                      border border-semanticError text-semanticError
                      hover:bg-red300 hover:text-white disabled:opacity-40
                    `}
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
        />

        {error && (
          <div className="mt-8px text-style-body2 text-semanticError">{error}</div>
        )}

        <div className="mt-8px text-style-body2 text-textColorText3">
          사진만 바뀝니다. 점수·랭킹·알림은 그대로이고, 원본 파일은 서버에 남습니다.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-16px pt-16px border-t border-brandPrimary-5%">
        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            방문일
          </label>
          <div className="text-style-body2 text-textColorText2">
            {review.visit_date ? formatDate(review.visit_date) : '-'}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            내부 점수
          </label>
          <div className="text-style-body2 text-textColorText2">
            {review.internal_score != null
              ? formatScoreOneDecimal(review.internal_score)
              : '-'}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            작성일
          </label>
          <div className="text-style-body2 text-textColorText2">
            {formatDate(review.created_at)}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            수정일
          </label>
          <div className="text-style-body2 text-textColorText2">
            {formatDate(review.updated_at)}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-24px pt-24px border-t border-brandPrimary-5%">
        <button
          onClick={onClose}
          className={`
            py-10px
            px-20px
            bg-4B3621-80%
            text-white
            rounded-8px
            text-style-button2
            hover:bg-4B3621-80%-70%
            transition-colors
          `}
        >
          닫기
        </button>
      </div>
    </div>
  )
}

export default AdminReviewDetailModal
